/**
 * Settings Page - Card Grid Layout for Master Role
 * Displays various setting categories as cards
 */
import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Avatar,
  Fade,
  Chip,
  Paper,
  IconButton,
} from '@mui/material';
import {
  VpnKey as KeyIcon,
  Code as CodeIcon,
  SmartToy as AIIcon,
  Settings as SettingsIcon,
  Psychology as PromptIcon,
  ArrowBack as BackIcon,
  Storage as McpIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function SettingsPage() {
  const navigate = useNavigate();
  const { t } = useTranslation(['common', 'settings']);

  // Settings categories configuration
  const settingCards = [
    {
      id: 'api-keys',
      title: t('apiKeys.title', { ns: 'settings' }),
      description: t('apiKeys.description', { ns: 'settings' }),
      icon: <KeyIcon sx={{ fontSize: 40 }} />,
      color: '#667eea',
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      path: '/settings/api-keys',
      available: true,
    },
    {
      id: 'agent-prompts',
      title: t('agentPrompts.title', { ns: 'settings' }),
      description: t('agentPrompts.description', { ns: 'settings' }),
      icon: <PromptIcon sx={{ fontSize: 40 }} />,
      color: '#764ba2',
      gradient: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
      path: '/settings/agent-prompts',
      available: true,
    },
    {
      id: 'mcp-config',
      title: t('mcpConfig.title', { ns: 'settings' }),
      description: t('mcpConfig.description', { ns: 'settings' }),
      icon: <McpIcon sx={{ fontSize: 40 }} />,
      color: '#11998e',
      gradient: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
      path: '/settings/mcp-configuration',
      available: true,
    },
    {
      id: 'prompt-templates',
      title: t('promptTemplates.title', { ns: 'settings' }),
      description: t('promptTemplates.description', { ns: 'settings' }),
      icon: <CodeIcon sx={{ fontSize: 40 }} />,
      color: '#f57c00',
      gradient: 'linear-gradient(135deg, #f57c00 0%, #ff6f00 100%)',
      path: '/settings/prompts',
      available: false,
    },
    {
      id: 'ai-providers',
      title: t('aiProviders.title', { ns: 'settings' }),
      description: t('aiProviders.description', { ns: 'settings' }),
      icon: <AIIcon sx={{ fontSize: 40 }} />,
      color: '#2e7d32',
      gradient: 'linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)',
      path: '/settings/ai-providers',
      available: false,
    },
  ];

  const handleCardClick = (card) => {
    if (card.available) {
      navigate(card.path);
    }
  };

  return (
    <Box
      sx={{
        height: '100%',
        overflow: 'auto',
        bgcolor: '#f5f7fa',
      }}
    >
      {/* Header - Modern Design */}
      <Paper
        elevation={0}
        sx={{
          mb: 4,
          p: 3,
          borderRadius: 3,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Background decoration */}
        <Box
          sx={{
            position: 'absolute',
            top: -50,
            right: -50,
            width: 200,
            height: 200,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.1)',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: -30,
            right: 100,
            width: 100,
            height: 100,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.05)',
          }}
        />

        <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative', zIndex: 1 }}>
          <IconButton
            onClick={() => navigate('/chat')}
            sx={{
              mr: 2,
              color: 'white',
              bgcolor: 'rgba(255,255,255,0.1)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }
            }}
          >
            <BackIcon />
          </IconButton>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: 2,
              bgcolor: 'rgba(255,255,255,0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mr: 2,
            }}
          >
            <SettingsIcon sx={{ fontSize: 32 }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4" fontWeight="600">{t('title', { ns: 'settings' })}</Typography>
            <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
              {t('subtitle', { ns: 'settings' })} • {settingCards.filter(c => c.available).length} {t('modulesAvailable', { ns: 'settings' })}
            </Typography>
          </Box>
        </Box>
      </Paper>

      <Container maxWidth="xl" sx={{ py: 0 }}>

        {/* Settings Cards Grid */}
        <Grid container spacing={4}>
          {settingCards.map((card, index) => (
            <Grid item xs={12} sm={6} md={4} key={card.id}>
              <Fade in timeout={300 + index * 100}>
                <Card
                  sx={{
                    height: '100%',
                    minHeight: 280,
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
                    borderRadius: 3,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                    opacity: card.available ? 1 : 0.6,
                    '&:hover': card.available
                      ? {
                        transform: 'translateY(-12px) scale(1.02)',
                        boxShadow: `0 16px 32px ${card.color}30`,
                      }
                      : {},
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '5px',
                      background: card.gradient,
                    },
                  }}
                >
                  <CardActionArea
                    onClick={() => handleCardClick(card)}
                    disabled={!card.available}
                    sx={{
                      height: '100%',
                      cursor: card.available ? 'pointer' : 'not-allowed',
                      '&:hover .MuiCardContent-root': {
                        bgcolor: card.available ? 'rgba(255,255,255,0.05)' : 'transparent',
                      },
                    }}
                  >
                    <CardContent sx={{ p: 3, height: '100%' }}>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          height: '100%',
                          gap: 2,
                        }}
                      >
                        {/* Icon */}
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                          }}
                        >
                          <Avatar
                            sx={{
                              width: 68,
                              height: 68,
                              background: card.gradient,
                              boxShadow: `0 6px 16px ${card.color}50`,
                              transition: 'transform 0.3s ease',
                              '&:hover': {
                                transform: 'scale(1.1) rotate(5deg)',
                              },
                            }}
                          >
                            {card.icon}
                          </Avatar>
                          {!card.available && (
                            <Chip
                              label={t('comingSoon', { ns: 'settings' })}
                              size="small"
                              sx={{
                                bgcolor: '#ffa726',
                                color: 'white',
                                fontWeight: 600,
                                fontSize: '0.7rem',
                              }}
                            />
                          )}
                        </Box>

                        {/* Content */}
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 600,
                              color: '#1a1a2e',
                              mb: 1,
                            }}
                          >
                            {card.title}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              lineHeight: 1.6,
                            }}
                          >
                            {card.description}
                          </Typography>
                        </Box>

                        {/* Footer indicator */}
                        {card.available && (
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'flex-end',
                              pt: 1,
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                color: card.color,
                                fontWeight: 600,
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                              }}
                            >
                              {t('configure', { ns: 'settings' })} →
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Fade>
            </Grid>
          ))}
        </Grid>

        {/* Info Box */}
        <Fade in timeout={800}>
          <Paper
            elevation={0}
            sx={{
              mt: 4,
              p: 3,
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  bgcolor: 'primary.50',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <SettingsIcon sx={{ fontSize: 22, color: 'primary.main' }} />
              </Box>
              <Typography variant="body2" color="text.secondary">
                {t('infoMessage', { ns: 'settings' })}
              </Typography>
            </Box>
          </Paper>
        </Fade>
      </Container>
    </Box>
  );
}


